// ÁTTEKINTÉS — „Saját jegyeim" csempe: a „Rám vár" csak a darabszámot adja, a support-kolléga napi kérdése viszont az,
// MELYIK jegyek ezek. Ugyanabból a végpontból él, mint a jegy-lista (`GET /tickets?mine=1&group=in_progress`) — nincs új
// BFF-munka és nincs drift. A sor a jegy deep-linkje; a „Mind megnyitása" a `mine=1` szűrt listára visz.
import { CheckCircleIcon } from '@heroicons/react/20/solid';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { CardRow, CardRows } from '@/components/shared/card-rows';
import { PanelCard, PanelCardEmpty } from '@/components/shared/panel-card';
import { TicketStatusBadge } from '@/components/tickets/ticket-status-badge';
import { dashboardKeys } from '@/features/dashboard/api';
import { api } from '@/lib/api';
import { formatRelativeTime } from '@/lib/format-datetime';

/** Hány jegyet mutat a csempe (a teljes lista a „Mind megnyitása" mögött). */
export const MY_TICKETS_LIMIT = 5;

export function MyTicketsCard(): JSX.Element {
  const { t, i18n } = useTranslation();
  const q = api.tickets.useQuery(
    dashboardKeys.myTickets,
    // `mine=1`: a jegy-lista saját „csak az enyém" kapcsolója (a `mine` az URL-ben sztring).
    // Rendezés: a lista-sor nem hordoz `updatedAt`-et, ezért a legfrissebben LÉTREHOZOTT jegyek elöl.
    {
      query: {
        mine: true,
        group: 'in_progress',
        sort: 'createdAt',
        order: 'desc',
        limit: MY_TICKETS_LIMIT,
      },
    },
    { queryKey: dashboardKeys.myTickets },
  );
  const tickets = q.data?.status === 200 ? q.data.body.tickets : [];

  return (
    <PanelCard
      capped
      title={t('dashboard.tiles.myTickets.title')}
      openAllTo="/tickets"
      openAllSearch={{ mine: '1', group: 'in_progress' }}
      loading={q.isLoading}
      skeletonRows={3}
    >
      {tickets.length === 0 ? (
        <PanelCardEmpty>
          <span className="flex items-center gap-2">
            <CheckCircleIcon className="text-success size-5 shrink-0" aria-hidden />
            {t('dashboard.myTickets.empty')}
          </span>
        </PanelCardEmpty>
      ) : (
        <CardRows>
          {tickets.map((tk) => {
            const meta = [
              tk.accountName ?? tk.clientName,
              tk.createdAt ? formatRelativeTime(tk.createdAt, i18n.language) : null,
            ]
              .filter((x): x is string => typeof x === 'string' && x !== '')
              .join(' · ');
            return (
              <CardRow key={tk.id}>
                <Link
                  to="/tickets/$id"
                  params={{ id: tk.id }}
                  className="hover:bg-accent/60 focus-visible:ring-ring flex items-center gap-3 px-4 py-2.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-md text-foreground truncate">{tk.subject}</div>
                    {meta !== '' && (
                      <div className="text-muted-foreground truncate text-xs">{meta}</div>
                    )}
                  </div>
                  <TicketStatusBadge
                    status={tk.status}
                    label={t(`tickets.statusLabel.${tk.status}`, { defaultValue: tk.status })}
                  />
                </Link>
              </CardRow>
            );
          })}
        </CardRows>
      )}
    </PanelCard>
  );
}
